// Stars for creator ratings (Brúnó, 2026-10-12): the average a brand reads on
// a creator's card and profile, and the five buttons it rates with.

import {useState} from 'react';
import type {BrandRatingSummary} from '../../shared/brand-api';
import {Icon} from '../creator/components/Icon';

/** "4.8 (12)" with a filled star, or "No ratings yet" for a creator nobody rated. */
export function RatingBadge({rating, large = false}: {rating: BrandRatingSummary; large?: boolean}) {
  if (rating.average === null) return <span className={`rating-badge rating-none${large ? ' large' : ''}`}>No ratings yet</span>;
  return <span className={`rating-badge${large ? ' large' : ''}`} aria-label={`Rated ${rating.average.toFixed(1)} out of 5 by ${rating.count} ${rating.count === 1 ? 'brand' : 'brands'}`}>
    <Icon name="star" size={large ? 18 : 15} fill="currentColor" strokeWidth={1.2} /><b>{rating.average.toFixed(1)}</b><small>({rating.count})</small>
  </span>;
}

/** A row of five stars, the first `value` filled. */
export function StarRow({value, size = 15}: {value: number; size?: number}) {
  return <span className="star-row" aria-label={`${value} out of 5 stars`}>{[1, 2, 3, 4, 5].map((n) =>
    <Icon key={n} name="star" size={size} fill={n <= value ? 'currentColor' : 'none'} strokeWidth={1.4} className={n <= value ? 'on' : undefined} />)}</span>;
}

const WORDS = ['', 'Poor', 'Below expectations', 'Good', 'Very good', 'Excellent'];

/** Five buttons to rate with; hovering shows what a click would give. */
export function StarPicker({value, onChange, disabled}: {value: number; onChange(stars: number): void; disabled?: boolean}) {
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return <div className="star-picker" role="radiogroup" aria-label="Your rating" onMouseLeave={() => setHover(0)}>
    {[1, 2, 3, 4, 5].map((n) => <button key={n} type="button" role="radio" aria-checked={value === n} aria-label={`${n} ${n === 1 ? 'star' : 'stars'}: ${WORDS[n]}`}
      className={n <= shown ? 'on' : undefined} disabled={disabled} onMouseEnter={() => setHover(n)} onClick={() => onChange(n)}>
      <Icon name="star" size={26} fill={n <= shown ? 'currentColor' : 'none'} strokeWidth={1.4} />
    </button>)}
    <span className="star-picker-word">{WORDS[shown] || 'Choose stars'}</span>
  </div>;
}
